import TicketControlsAdmin from '@/components/TicketControlsAdmin';
import React, { useEffect, useState } from 'react';
import { useWSRefresh } from '../contexts/WebSocketContext';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Chip,
  Avatar,
  Skeleton,
  InputAdornment,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Timer as TimerIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Home as HomeIcon,
  LocationOn as LocationIcon,
  DevicesOther as DeviceIcon,
  ContactPhone as EmergencyIcon,
  Badge as BadgeIcon,
  Work as WorkIcon,
  Paid as PaidIcon,
} from '@mui/icons-material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import api from '../services/api';
import BillingRulesSettings from './BillingRulesSettings';
import VehiclesAdmin from './VehiclesAdmin';
import TimeclockAdmin from './TimeclockAdmin';
import RoleMailboxesSettings from './RoleMailboxesSettings';
import RoadSourcesSettings from './RoadSourcesSettings';   // road data source switches (2026-09-05)
import TrafficIcon from '@mui/icons-material/Traffic';   // role → shared mailbox map (2026-09-04)
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { usersApi, settingsApi, trackingApi, SystemSetting } from '../services/api';

interface User {
  id: number;
  email: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  work_phone?: string;
  work_email?: string;
  personal_phone?: string;
  personal_email?: string;
  home_address?: string;
  home_city?: string;
  home_state?: string;
  home_postal_code?: string;
  work_start_address?: string;
  work_start_city?: string;
  work_start_state?: string;
  work_start_postal_code?: string;
  device_id?: string;
  tracking_enabled?: boolean;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relationship?: string;
  employee_id?: string;
  hire_date?: string;
  job_title?: string;
  department?: string;
  role?: { id: number; name: string };
  is_active: boolean;
  created_at: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ py: 3 }}>
      {value === index && children}
    </Box>
  );
}

// User Management Component
function UserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [trackerDevices, setTrackerDevices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSev, setSnackSev] = useState<'success'|'error'>('success');
  const [snackOpen, setSnackOpen] = useState(false);
  const setSnack = (msg: string, sev: 'success'|'error') => { setSnackMsg(msg); setSnackSev(sev); setSnackOpen(true); };
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    role_ids: [2] as number[],
    password: '',
    is_active: true,
    // Work Contact
    work_phone: '',
    work_email: '',
    // Personal Contact
    personal_phone: '',
    personal_email: '',
    // Home Address
    home_address: '',
    home_city: '',
    home_state: '',
    home_postal_code: '',
    // Work Start Address
    work_start_address: '',
    work_start_city: '',
    work_start_state: '',
    work_start_postal_code: '',
    // Device Tracking
    device_id: '',
    tracking_enabled: false,
    // Emergency Contact
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relationship: '',
    // Employment
    employee_id: '',
    hire_date: '',
    job_title: '',
    department: '',
  });

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await usersApi.list();
      setUsers(res.data);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDevices = async () => {
    try { const r = await trackingApi.getDevices(); setTrackerDevices(r.data.devices || []); } catch {}
  };

  
  // WebSocket push — instant refresh on any change
  useWSRefresh(['user:updated', 'ticket:assigned'], () => { fetchUsers(); fetchDevices(); });

  useEffect(() => { fetchUsers(); fetchDevices(); const iv = setInterval(() => { fetchUsers(); fetchDevices(); }, 30000); return () => clearInterval(iv); }, []);

  const handleOpenDialog = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        role_ids: (() => { const inv: Record<string, number> = { SuperAdmin: 1, Admin: 2, Dispatcher: 3, Technician: 4, Viewer: 5 }; const ids = (((user as any).roles || []) as string[]).map((n) => inv[n]).filter(Boolean); return ids.length ? ids : [user.role?.id || 2]; })(),
        password: '',
        is_active: user.is_active,
        work_phone: user.work_phone || '',
        work_email: user.work_email || '',
        personal_phone: user.personal_phone || '',
        personal_email: user.personal_email || '',
        home_address: user.home_address || '',
        home_city: user.home_city || '',
        home_state: user.home_state || '',
        home_postal_code: user.home_postal_code || '',
        work_start_address: user.work_start_address || '',
        work_start_city: user.work_start_city || '',
        work_start_state: user.work_start_state || '',
        work_start_postal_code: user.work_start_postal_code || '',
        device_id: user.device_id || '',
        tracking_enabled: user.tracking_enabled || false,
        emergency_contact_name: user.emergency_contact_name || '',
        emergency_contact_phone: user.emergency_contact_phone || '',
        emergency_contact_relationship: user.emergency_contact_relationship || '',
        employee_id: user.employee_id || '',
        hire_date: user.hire_date || '',
        job_title: user.job_title || '',
        department: user.department || '',
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        first_name: '',
        last_name: '',
        phone: '',
        role_ids: [2],
        password: '',
        is_active: true,
        work_phone: '',
        work_email: '',
        personal_phone: '',
        personal_email: '',
        home_address: '',
        home_city: '',
        home_state: '',
        home_postal_code: '',
        work_start_address: '',
        work_start_city: '',
        work_start_state: '',
        work_start_postal_code: '',
        device_id: '',
        tracking_enabled: false,
        emergency_contact_name: '',
        emergency_contact_phone: '',
        emergency_contact_relationship: '',
        employee_id: '',
        hire_date: '',
        job_title: '',
        department: '',
      });
    }
    setDialogOpen(true);
  };

  // Map role_id to role name for backend
  const roleMap: Record<number, string> = { 1: 'SuperAdmin', 2: 'Admin', 3: 'Dispatcher', 4: 'Technician', 5: 'Viewer' };

  const handleSave = async () => {
    try {
      const roleNames = (formData.role_ids.length ? formData.role_ids : [2]).map((id) => roleMap[id]).filter(Boolean);
      if (editingUser) {
        // Build update payload matching backend UserUpdate schema
        const updatePayload: any = {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone,
          is_active: formData.is_active,
          roles: roleNames,
          work_phone: formData.work_phone || null,
          work_email: formData.work_email || null,
          personal_phone: formData.personal_phone || null,
          personal_email: formData.personal_email || null,
          home_address: formData.home_address || null,
          home_city: formData.home_city || null,
          home_state: formData.home_state || null,
          work_start_address: formData.work_start_address || null,
          work_start_city: formData.work_start_city || null,
          work_start_state: formData.work_start_state || null,
          device_id: formData.device_id || null,
          tracking_enabled: formData.tracking_enabled,
          employee_id: formData.employee_id || null,
          job_title: formData.job_title || null,
          department: formData.department || null,
        };
        await usersApi.update(editingUser.id, updatePayload);
        setSnack('User updated successfully', 'success');
      } else {
        // Build create payload
        const username = formData.email.split('@')[0].toLowerCase().replace(/[^a-z0-9]/g, '');
        const createPayload = {
          username,
          email: formData.email,
          password: formData.password || 'TempPass123!',
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone: formData.phone || null,
          roles: roleNames,
          job_title: formData.job_title || null,
          department: formData.department || null,
        };
        await usersApi.create(createPayload);
        setSnack('User created successfully', 'success');
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to save user';
      setSnack(typeof msg === 'string' ? msg : JSON.stringify(msg), 'error');
      console.error('Failed to save user:', err);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this user?')) return;
    try {
      await usersApi.delete(id);
      fetchUsers();
    } catch (err) {
      console.error('Failed to delete user:', err);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>User Management</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ background: 'linear-gradient(180deg, #1E4A6B, #0D2233)' }}
          >
            Add User
          </Button>
          <IconButton onClick={fetchUsers} disabled={loading} sx={{ bgcolor: '#f1f5f9' }}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      <TableContainer component={Card}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Work Email</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Work Phone</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Personal Phone</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Tracking</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={8}><Skeleton variant="rectangular" height={40} /></TableCell>
                </TableRow>
              ))
            ) : users.map(user => (
              <TableRow key={user.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ width: 36, height: 36, background: 'linear-gradient(180deg, #1E4A6B, #0D2233)' }}>
                      {(user.first_name?.[0] || '') + (user.last_name?.[0] || '') || user.email[0].toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {user.first_name} {user.last_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {user.employee_id || 'No Employee ID'}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>{user.work_email || user.email}</TableCell>
                <TableCell>{user.work_phone || '-'}</TableCell>
                <TableCell>{user.personal_phone || '-'}</TableCell>
                <TableCell>
                  {((((user as any).roles as string[]) || []).length ? ((user as any).roles as string[]) : [user.role?.name || 'User']).map((rn: string) => (
                    <Chip key={rn} label={rn} size="small" sx={{ mr: 0.5, mb: 0.25, fontWeight: 600,
                      bgcolor: rn === 'Admin' || rn === 'SuperAdmin' ? '#e0e7ff' : rn === 'Technician' ? '#d1fae5' : '#f3f4f6',
                      color: rn === 'Admin' || rn === 'SuperAdmin' ? '#4f46e5' : rn === 'Technician' ? '#059669' : '#6b7280' }} />
                  ))}
                </TableCell>
                <TableCell>
                  <Chip
                    label={user.tracking_enabled ? 'Enabled' : 'Disabled'}
                    size="small"
                    sx={{
                      bgcolor: user.tracking_enabled ? '#dcfce7' : '#f3f4f6',
                      color: user.tracking_enabled ? '#16a34a' : '#6b7280',
                      fontWeight: 600,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={user.is_active ? 'Active' : 'Inactive'}
                    size="small"
                    sx={{
                      bgcolor: user.is_active ? '#dcfce7' : '#fee2e2',
                      color: user.is_active ? '#16a34a' : '#dc2626',
                      fontWeight: 600,
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleOpenDialog(user)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => handleDelete(user.id)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Comprehensive User Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {/* Basic Information */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon color="primary" />
                  <Typography fontWeight="medium">Basic Information</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Login Email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      helperText="Used for system login"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Roles</InputLabel>
                      <Select
                        multiple
                        value={formData.role_ids}
                        label="Roles"
                        onChange={(e) => { const v = e.target.value as any; setFormData({ ...formData, role_ids: (typeof v === 'string' ? v.split(',').map(Number) : v) as number[] }); }}
                        renderValue={(sel) => (sel as number[]).map((id) => roleMap[id]).join(', ')}
                      >
                        <MenuItem value={1}>SuperAdmin</MenuItem>
                        <MenuItem value={2}>Admin</MenuItem>
                        <MenuItem value={3}>Dispatcher</MenuItem>
                        <MenuItem value={4}>Technician</MenuItem>
                        <MenuItem value={5}>Viewer</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {!editingUser && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Password"
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.is_active}
                          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        />
                      }
                      label="Active"
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Work Contact Information */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WorkIcon color="primary" />
                  <Typography fontWeight="medium">Work Contact Information</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Work Email"
                      type="email"
                      value={formData.work_email}
                      onChange={(e) => setFormData({ ...formData, work_email: e.target.value })}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><EmailIcon sx={{ color: 'action.active' }} /></InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Work Phone"
                      value={formData.work_phone}
                      onChange={(e) => setFormData({ ...formData, work_phone: e.target.value })}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: 'action.active' }} /></InputAdornment>,
                      }}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Personal Contact Information */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon color="primary" />
                  <Typography fontWeight="medium">Personal Contact Information</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Personal Email"
                      type="email"
                      value={formData.personal_email}
                      onChange={(e) => setFormData({ ...formData, personal_email: e.target.value })}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><EmailIcon sx={{ color: 'action.active' }} /></InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Personal Phone"
                      value={formData.personal_phone}
                      onChange={(e) => setFormData({ ...formData, personal_phone: e.target.value })}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: 'action.active' }} /></InputAdornment>,
                      }}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Home Address */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HomeIcon color="primary" />
                  <Typography fontWeight="medium">Home Address</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Street Address"
                      value={formData.home_address}
                      onChange={(e) => setFormData({ ...formData, home_address: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="City"
                      value={formData.home_city}
                      onChange={(e) => setFormData({ ...formData, home_city: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="State"
                      value={formData.home_state}
                      onChange={(e) => setFormData({ ...formData, home_state: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Postal Code"
                      value={formData.home_postal_code}
                      onChange={(e) => setFormData({ ...formData, home_postal_code: e.target.value })}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Work Start Address */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationIcon color="primary" />
                  <Typography fontWeight="medium">Work Start Address</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  The location where the technician starts their work day (used for routing calculations)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Street Address"
                      value={formData.work_start_address}
                      onChange={(e) => setFormData({ ...formData, work_start_address: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="City"
                      value={formData.work_start_city}
                      onChange={(e) => setFormData({ ...formData, work_start_city: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="State"
                      value={formData.work_start_state}
                      onChange={(e) => setFormData({ ...formData, work_start_state: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Postal Code"
                      value={formData.work_start_postal_code}
                      onChange={(e) => setFormData({ ...formData, work_start_postal_code: e.target.value })}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Device Tracking */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DeviceIcon color="primary" />
                  <Typography fontWeight="medium">Device Tracking</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Link a phone from the employee tracking system for real-time GPS location
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Tracker Device</InputLabel>
                      <Select
                        value={formData.device_id}
                        label="Tracker Device"
                        onChange={(e) => setFormData({ ...formData, device_id: e.target.value as string, tracking_enabled: !!e.target.value })}
                      >
                        <MenuItem value=""><em>No device assigned</em></MenuItem>
                        {trackerDevices.map((d: any) => (
                          <MenuItem key={d.device_id} value={d.device_id} disabled={!!(d.assigned_to && d.assigned_to.user_id !== editingUser?.id)}>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <Typography variant="body2" fontWeight={600}>
                                {d.device_name || 'Unknown Device'}
                                {d.assigned_to && d.assigned_to.user_id !== editingUser?.id && (
                                  <Chip label={`Used by ${d.assigned_to.user_name}`} size="small" sx={{ ml: 1, height: 18, fontSize: 10 }} />
                                )}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {d.device_id} · {d.status} · Last seen: {d.last_seen ? new Date(d.last_seen).toLocaleString() : 'Never'}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.tracking_enabled}
                          onChange={(e) => setFormData({ ...formData, tracking_enabled: e.target.checked })}
                        />
                      }
                      label="Enable Location Tracking"
                    />
                    {formData.device_id && formData.tracking_enabled && (
                      <Chip label="GPS Active" color="success" size="small" sx={{ ml: 1 }} />
                    )}
                    {formData.device_id && !formData.tracking_enabled && (
                      <Chip label="Tracking Paused" color="warning" size="small" sx={{ ml: 1 }} />
                    )}
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Emergency Contact */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmergencyIcon color="primary" />
                  <Typography fontWeight="medium">Emergency Contact</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Contact Name"
                      value={formData.emergency_contact_name}
                      onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Contact Phone"
                      value={formData.emergency_contact_phone}
                      onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Relationship"
                      value={formData.emergency_contact_relationship}
                      onChange={(e) => setFormData({ ...formData, emergency_contact_relationship: e.target.value })}
                      placeholder="e.g., Spouse, Parent, Sibling"
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Employment Details */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BadgeIcon color="primary" />
                  <Typography fontWeight="medium">Employment Details</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Employee ID"
                      value={formData.employee_id}
                      onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Hire Date"
                      type="date"
                      value={formData.hire_date}
                      onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Job Title"
                      value={formData.job_title}
                      onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Department"
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{ background: 'linear-gradient(180deg, #1E4A6B, #0D2233)' }}
          >
            {editingUser ? 'Save Changes' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackOpen} autoHideDuration={4000} onClose={() => setSnackOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackOpen(false)} severity={snackSev} sx={{ width: '100%' }}>{snackMsg}</Alert>
      </Snackbar>
    </Box>
  );
}

// Aging Alert Settings Component
function AgingAlertSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [settings, setSettings] = useState({
    aging_blink_days: '3',
    aging_warning_days: '5',
    aging_critical_days: '7',
    aging_blink_enabled: 'true',
    aging_color_change_enabled: 'true',
    aging_warning_color: '#f59e0b',
    aging_critical_color: '#dc2626',
  });

  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      try {
        const res = await settingsApi.getAll();
        const settingsMap: Record<string, string> = {};
        res.data.forEach((s: SystemSetting) => {
          settingsMap[s.key] = s.value || '';
        });
        setSettings(prev => ({
          ...prev,
          ...settingsMap,
        }));
      } catch (err) {
        console.error('Failed to fetch settings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSuccess(false);
    try {
      await Promise.all(
        Object.entries(settings).map(([key, value]) =>
          settingsApi.update(key, value)
        )
      );
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save settings:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Ticket Aging Alerts</Typography>
          <Typography variant="body2" color="text.secondary">
            Configure visual alerts for aging tickets to help schedulers identify calls that need attention
          </Typography>
        </Box>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Settings saved successfully!
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Blink Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#fef3c7' }}>
                  <TimerIcon sx={{ color: '#d97706' }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Blink Alert</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Make ticket cards blink to draw attention
                  </Typography>
                </Box>
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.aging_blink_enabled === 'true'}
                    onChange={(e) => setSettings({ ...settings, aging_blink_enabled: e.target.checked ? 'true' : 'false' })}
                  />
                }
                label="Enable blink alert"
                sx={{ mb: 2, display: 'block' }}
              />

              <TextField
                fullWidth
                label="Start blinking after (days)"
                type="number"
                value={settings.aging_blink_days}
                onChange={(e) => setSettings({ ...settings, aging_blink_days: e.target.value })}
                InputProps={{
                  endAdornment: <InputAdornment position="end">days</InputAdornment>,
                }}
                disabled={settings.aging_blink_enabled !== 'true'}
                helperText="Ticket cards will start blinking after this many days without progress"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Color Change Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#fee2e2' }}>
                  <WarningIcon sx={{ color: '#dc2626' }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Color Change Alert</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Change ticket card colors based on age
                  </Typography>
                </Box>
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.aging_color_change_enabled === 'true'}
                    onChange={(e) => setSettings({ ...settings, aging_color_change_enabled: e.target.checked ? 'true' : 'false' })}
                  />
                }
                label="Enable color change alert"
                sx={{ mb: 2, display: 'block' }}
              />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Warning after (days)"
                    type="number"
                    value={settings.aging_warning_days}
                    onChange={(e) => setSettings({ ...settings, aging_warning_days: e.target.value })}
                    disabled={settings.aging_color_change_enabled !== 'true'}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">Color:</Typography>
                    <input
                      type="color"
                      value={settings.aging_warning_color}
                      onChange={(e) => setSettings({ ...settings, aging_warning_color: e.target.value })}
                      disabled={settings.aging_color_change_enabled !== 'true'}
                      style={{ width: 50, height: 36, cursor: 'pointer', border: 'none' }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Critical after (days)"
                    type="number"
                    value={settings.aging_critical_days}
                    onChange={(e) => setSettings({ ...settings, aging_critical_days: e.target.value })}
                    disabled={settings.aging_color_change_enabled !== 'true'}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">Color:</Typography>
                    <input
                      type="color"
                      value={settings.aging_critical_color}
                      onChange={(e) => setSettings({ ...settings, aging_critical_color: e.target.value })}
                      disabled={settings.aging_color_change_enabled !== 'true'}
                      style={{ width: 50, height: 36, cursor: 'pointer', border: 'none' }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Preview */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Preview</Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Card sx={{ minWidth: 150, p: 2 }}>
                  <Typography variant="caption" color="text.secondary">Normal Ticket</Typography>
                  <Typography variant="body2">(0-{settings.aging_warning_days} days)</Typography>
                </Card>
                <Card sx={{ minWidth: 150, p: 2, bgcolor: settings.aging_warning_color, color: 'white' }}>
                  <Typography variant="caption">Warning Ticket</Typography>
                  <Typography variant="body2">({settings.aging_warning_days}-{settings.aging_critical_days} days)</Typography>
                </Card>
                <Card sx={{ minWidth: 150, p: 2, bgcolor: settings.aging_critical_color, color: 'white' }}>
                  <Typography variant="caption">Critical Ticket</Typography>
                  <Typography variant="body2">(&gt;{settings.aging_critical_days} days)</Typography>
                </Card>
                <Card
                  sx={{
                    minWidth: 150,
                    p: 2,
                    animation: settings.aging_blink_enabled === 'true' ? 'blink 1s infinite' : 'none',
                    '@keyframes blink': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.5 },
                    },
                  }}
                >
                  <Typography variant="caption" color="text.secondary">Blinking Ticket</Typography>
                  <Typography variant="body2">(&gt;{settings.aging_blink_days} days)</Typography>
                </Card>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{ background: 'linear-gradient(180deg, #1E4A6B, #0D2233)' }}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>
    </Box>
  );
}

// General Settings Component
function GeneralSettings() {
  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>General Settings</Typography>
      <Alert severity="info">
        General settings configuration coming soon.
      </Alert>
    </Box>
  );
}

// Notification Settings Component
function NotificationSettings() {
  const [prefs, setPrefs] = React.useState<any>(null);
  const [subStatus, setSubStatus] = React.useState<any>(null);
  const [saving, setSaving] = React.useState(false);
  const [testResult, setTestResult] = React.useState('');

  React.useEffect(() => {
    api.get('/notifications/preferences').then(r => setPrefs(r.data)).catch(() => {});
    api.get('/notifications/subscription-status').then(r => setSubStatus(r.data)).catch(() => {});
  }, []);

  const updatePref = async (key: string, val: boolean) => {
    const updated = { ...prefs, [key]: val ? 1 : 0 };
    setPrefs(updated);
    setSaving(true);
    try { await api.put('/notifications/preferences', { [key]: val }); } catch {}
    setSaving(false);
  };

  const testPush = async () => {
    setTestResult('Sending...');
    try {
      const r = await api.post('/notifications/test-push', { title: 'Test Push', body: 'If you see this, push notifications are working!' });
      setTestResult(`Sent to ${r.data.sent_to} device(s)`);
    } catch { setTestResult('Failed to send'); }
  };

  if (!prefs) return <Box sx={{ p: 2 }}><Typography>Loading...</Typography></Box>;

  const prefItems = [
    { key: 'push_enabled', label: 'Push Notifications', desc: 'Receive browser push notifications' },
    { key: 'ticket_assigned', label: 'Ticket Assigned', desc: 'When a ticket is assigned to you' },
    { key: 'ticket_status_change', label: 'Status Changes', desc: 'When a ticket status changes' },
    { key: 'ticket_completed', label: 'Ticket Completed', desc: 'When a ticket is marked complete' },
    { key: 'ticket_created', label: 'New Tickets', desc: 'When a new ticket is created' },
    { key: 'schedule_change', label: 'Schedule Changes', desc: 'When your schedule is updated' },
    { key: 'system_alerts', label: 'System Alerts', desc: 'System and maintenance alerts' },
    // incoming alerts (dashboard Recent Alerts card) 2026-09-04
    { key: 'incoming_documents', label: 'Incoming documents, pictures & emails', desc: 'Anything that arrives from a customer, tech@ email or the portal (in-app + push)' },
    { key: 'incoming_tickets', label: 'Incoming portal requests', desc: 'New service requests submitted through the customer portal (in-app + push)' },
    { key: 'email_incoming', label: 'Also email me incoming alerts', desc: 'Sent to your work email through the Concept mail relay — obeys the system-wide Email switch in admin.concept.bz' },
    { key: 'sms_incoming', label: 'Also text me incoming alerts', desc: 'Sent to your work phone — obeys the system-wide SMS switch in admin.concept.bz' },
  ];

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Notification Settings</Typography>

      <Card sx={{ mb: 3 }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>Notification Preferences</Typography>
          {prefItems.map(item => (
            <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box>
                <Typography variant="body2" fontWeight={500}>{item.label}</Typography>
                <Typography variant="caption" color="text.secondary">{item.desc}</Typography>
              </Box>
              <Switch checked={!!prefs[item.key]} onChange={(e) => updatePref(item.key, e.target.checked)} disabled={saving} />
            </Box>
          ))}
        </Box>
      </Card>

      <Card sx={{ mb: 3 }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>Push Subscription Status</Typography>
          {subStatus?.subscriptions?.length ? (
            subStatus.subscriptions.map((s: any, i: number) => (
              <Box key={i} sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" fontWeight={500}>
                  Device {i + 1} {s.is_active ? '(Active)' : '(Inactive)'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Last used: {s.last_used_at ? new Date(s.last_used_at).toLocaleString() : 'Never'}
                </Typography>
              </Box>
            ))
          ) : (
            <Alert severity="info" sx={{ mt: 1 }}>
              No push subscriptions. Click the bell icon and enable push notifications in your browser.
            </Alert>
          )}
        </Box>
      </Card>

      <Card>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button variant="outlined" onClick={testPush}>Send Test Push</Button>
          {testResult && <Typography variant="body2" color="text.secondary">{testResult}</Typography>}
        </Box>
      </Card>
    </Box>
  );
}

// Main Admin Page Component
export default function AdminPage() {
  // deep-link support for the Time Clock nav entry (/admin?tab=timeclock)
  const [tabValue, setTabValue] = useState(
    typeof window !== 'undefined' && window.location.search.indexOf('tab=timeclock') >= 0 ? 6 : window.location.search.indexOf('tab=mailboxes') >= 0 ? 7 : window.location.search.indexOf('tab=roaddata') >= 0 ? 8 : 0);
  useEffect(() => {
    const sync = () => { if (window.location.search.indexOf('tab=timeclock') >= 0) setTabValue(6); else if (window.location.search.indexOf('tab=mailboxes') >= 0) setTabValue(7); else if (window.location.search.indexOf('tab=roaddata') >= 0) setTabValue(8); };
    sync();
    window.addEventListener('popstate', sync);
    return () => window.removeEventListener('popstate', sync);
  }, []);

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
        Administration
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage users, settings, and system configuration
      </Typography>

      <Card sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': { fontWeight: 600 },
          }}
        >
          <Tab icon={<PersonIcon />} iconPosition="start" label="Users" />
          <Tab icon={<TimerIcon />} iconPosition="start" label="Aging Alerts" />
          <Tab label="General" />
          <Tab label="Notifications" />
          <Tab icon={<PaidIcon />} iconPosition="start" label="Billing Rules" />
          <Tab icon={<DirectionsCarIcon />} iconPosition="start" label="Vehicles" />
          <Tab icon={<AccessTimeIcon />} iconPosition="start" label="Time Clock" />
          <Tab icon={<MailOutlineIcon />} iconPosition="start" label="Role Mailboxes" />
          <Tab icon={<TrafficIcon />} iconPosition="start" label="Road Data" />
          <Tab label="Ticket Controls" />
        </Tabs>
      </Card>

      <TabPanel value={tabValue} index={0}>
        <UserManagement />
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <AgingAlertSettings />
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        <GeneralSettings />
      </TabPanel>
      <TabPanel value={tabValue} index={3}>
        <NotificationSettings />
      </TabPanel>
      <TabPanel value={tabValue} index={4}>
        <BillingRulesSettings />
      </TabPanel>
      <TabPanel value={tabValue} index={5}>
        <VehiclesAdmin />
      </TabPanel>
      <TabPanel value={tabValue} index={6}>
        <TimeclockAdmin />
      </TabPanel>
      <TabPanel value={tabValue} index={7}>
        <RoleMailboxesSettings />
      </TabPanel>
      <TabPanel value={tabValue} index={9}>
        <TicketControlsAdmin />
      </TabPanel>
      <TabPanel value={tabValue} index={8}>
        <RoadSourcesSettings />
      </TabPanel>
    </Box>
  );
}
